import { useMemo, useState } from "react";
import { MapPin, Compass, Radar, TrendingUp, Zap } from "lucide-react";

// ---------------------------------------------------------------
// Sterling · Local Node · Street Strike Map
// Zip-based visualisation of the "Anchor → Titan" upsell path.
// Ships with mock local shops keyed off the zip. No real geo API.
// Intended for use inside /training as a rep drill.
// ---------------------------------------------------------------

const TIERS = [
  { id: "anchor",   label: "Anchor",   price: "$97/mo",     accent: "#34D399", desc: "First install — concierge line, review capture, 24/7 pickup." },
  { id: "atlas",    label: "Atlas",    price: "$297/mo",    accent: "#60A5FA", desc: "Add mascot + follow-up loop. Grid Readiness rises past 60." },
  { id: "commander",label: "Commander",price: "$697/mo",    accent: "#F5C842", desc: "Dedicated Coordinator inbox, GVO Auto-Net, full analytics rail." },
  { id: "titan",    label: "Titan",    price: "$1,997/mo",  accent: "#F97316", desc: "Multi-location, ambassador program, quarterly capsule drops." },
];

// Deterministic pseudo-random from zip so the map looks alive but stable
// while the operator drills the same code repeatedly.
function seededShops(zip) {
  const trades = ["Auto Shop", "HVAC", "Roofing", "Landscaper", "Plumbing", "Boutique", "Cleaning Services", "Band"];
  const streets = ["Main St", "Cedar Ave", "3rd Ave", "Oak Rd", "Elm St", "River Rd", "Front St", "Vine St"];
  const seed = String(zip || "00000").padEnd(5, "0").split("").map((c) => parseInt(c, 10) || 0);
  const shopCount = 5 + (seed[3] % 3); // 5-7 shops
  const shops = [];
  for (let i = 0; i < shopCount; i += 1) {
    const trade = trades[(seed[i % 5] + i) % trades.length];
    const street = streets[(seed[(i + 2) % 5] + i * 3) % streets.length];
    const number = 100 + ((seed[(i + 1) % 5] + i * 17) % 800);
    // Coordinates constrained to the SVG canvas (with padding).
    const x = 90 + ((seed[(i + 4) % 5] * 47 + i * 91) % 460);
    const y = 60 + ((seed[(i) % 5] * 31 + i * 63) % 260);
    const t = i === 0 ? "anchor" : i < 3 ? "atlas" : i < 5 ? "commander" : "titan";
    shops.push({
      id: `shop-${i}`,
      name: `${trade} · ${number} ${street}`,
      trade,
      street: `${number} ${street}`,
      x,
      y,
      tier: t,
    });
  }
  return shops;
}

export default function StreetStrikeMap() {
  const [zip, setZip] = useState("");
  const [committed, setCommitted] = useState("");
  const [activeShop, setActiveShop] = useState(null);

  const shops = useMemo(() => (committed ? seededShops(committed) : []), [committed]);
  const active = useMemo(
    () => shops.find((s) => s.id === activeShop) || null,
    [shops, activeShop],
  );

  const commit = (e) => {
    e?.preventDefault?.();
    const z = zip.replace(/\D+/g, "").slice(0, 5);
    if (z.length < 5) return;
    setCommitted(z);
    setActiveShop(null);
  };

  return (
    <div
      className="rounded-sm border p-5 sm:p-6 relative overflow-hidden"
      style={{
        borderColor: "rgba(52,211,153,0.4)",
        background:
          "linear-gradient(180deg, rgba(16,185,129,0.08) 0%, rgba(6,11,20,0.9) 60%, rgba(6,11,20,1) 100%)",
        boxShadow: "0 24px 60px -24px rgba(52,211,153,0.35)",
      }}
      data-testid="street-strike-map"
    >
      <div className="hud-label text-[10px] mb-2 flex items-center gap-2 text-emerald-300">
        <Radar size={11} /> Local Node · Street Strike
      </div>
      <h2 className="font-display text-2xl sm:text-3xl text-white leading-tight">
        Anchor to <span className="text-gold italic">Titan</span> — pick a zip, run the block.
      </h2>
      <p className="mt-3 text-slate-300 text-sm leading-relaxed">
        Drop a zip and the map spins up local shops. Every dot is a $97
        <span className="text-emerald-300"> Anchor</span> door. The colour
        shows the upsell ladder they climb once the Grid installs.
      </p>

      {/* Zip input */}
      <form onSubmit={commit} className="mt-5 flex items-stretch gap-2">
        <div className="flex-1 relative">
          <MapPin
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-emerald-300"
          />
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]{5}"
            value={zip}
            onChange={(e) => setZip(e.target.value.replace(/\D+/g, "").slice(0, 5))}
            placeholder="Try 43302, 44691, 43015…"
            data-testid="street-strike-zip-input"
            className="w-full bg-black/40 border border-emerald-500/30 focus:border-emerald-400 outline-none pl-9 pr-3 py-3 rounded-sm text-white text-sm placeholder:text-slate-600 min-h-[44px]"
          />
        </div>
        <button
          type="submit"
          disabled={zip.length < 5}
          data-testid="street-strike-scan"
          className="btn-gold px-4 sm:px-5 py-3 rounded-sm text-xs uppercase tracking-[0.24em] flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed min-h-[44px]"
        >
          <Zap size={12} /> Scan
        </button>
      </form>

      {/* Map canvas */}
      <div className="mt-5 relative rounded-sm border border-white/10 overflow-hidden">
        <svg
          viewBox="0 0 640 380"
          className="w-full h-auto block bg-[#04070C]"
          data-testid="street-strike-svg"
        >
          <defs>
            <linearGradient id="street-strike-grid" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#34D399" stopOpacity="0.16" />
              <stop offset="100%" stopColor="#D4AF37" stopOpacity="0.06" />
            </linearGradient>
          </defs>

          {/* Base grid */}
          <rect width="640" height="380" fill="url(#street-strike-grid)" />
          {[...Array(13)].map((_, i) => (
            <line key={`v-${i}`} x1={i * 50} y1="0" x2={i * 50} y2="380"
              stroke="#34D399" strokeOpacity="0.10" strokeWidth="0.6" />
          ))}
          {[...Array(8)].map((_, i) => (
            <line key={`h-${i}`} x1="0" y1={i * 50} x2="640" y2={i * 50}
              stroke="#34D399" strokeOpacity="0.10" strokeWidth="0.6" />
          ))}

          {/* Faux street ribbons */}
          <g stroke="#94A3B8" strokeOpacity="0.28" strokeWidth="1.4" fill="none">
            <path d="M40 200 Q 200 140 400 200 T 620 210" />
            <path d="M50 90 Q 240 130 420 100 T 620 120" />
            <path d="M60 310 Q 220 260 400 300 T 620 300" />
            <path d="M180 30 L 180 360" strokeDasharray="4 6" />
            <path d="M440 30 L 440 360" strokeDasharray="4 6" />
          </g>

          {/* HQ pin */}
          <g>
            <circle cx="60" cy="60" r="10" fill="#D4AF37" fillOpacity="0.15" stroke="#D4AF37" strokeWidth="1.4">
              <animate attributeName="r" from="6" to="26" dur="3.2s" repeatCount="indefinite" />
              <animate attributeName="stroke-opacity" from="0.9" to="0" dur="3.2s" repeatCount="indefinite" />
            </circle>
            <circle cx="60" cy="60" r="6" fill="#D4AF37" />
            <text x="76" y="58" fill="#F8FAFC" fontSize="11" fontFamily="Outfit, sans-serif">
              HQ · Marion Node
            </text>
            <text x="76" y="72" fill="#94A3B8" fontSize="9" letterSpacing="1.5" fontFamily="Manrope, sans-serif">
              LAST 50 MILES
            </text>
          </g>

          {/* Shops */}
          {shops.map((s) => {
            const tier = TIERS.find((t) => t.id === s.tier) || TIERS[0];
            const isActive = activeShop === s.id;
            return (
              <g
                key={s.id}
                onClick={() => setActiveShop(s.id)}
                style={{ cursor: "pointer" }}
                data-testid={`street-strike-shop-${s.id}`}
              >
                <circle
                  cx={s.x}
                  cy={s.y}
                  r={isActive ? 14 : 10}
                  fill={tier.accent}
                  fillOpacity={isActive ? 0.28 : 0.16}
                  stroke={tier.accent}
                  strokeWidth={isActive ? 1.8 : 1.2}
                />
                <circle cx={s.x} cy={s.y} r="4" fill={tier.accent} />
                {isActive && (
                  <text
                    x={s.x + 10}
                    y={s.y - 8}
                    fill="#F8FAFC"
                    fontSize="10"
                    fontFamily="Outfit, sans-serif"
                  >
                    {s.trade}
                  </text>
                )}
              </g>
            );
          })}

          {!committed && (
            <text
              x="320" y="200"
              textAnchor="middle"
              fill="#475569"
              fontSize="14"
              fontFamily="Manrope, sans-serif"
              letterSpacing="3"
            >
              ENTER A ZIP TO REVEAL THE BLOCK
            </text>
          )}
        </svg>

        {/* Legend */}
        <div
          className="absolute bottom-2 left-2 right-2 sm:right-auto sm:left-2 flex flex-wrap gap-2 px-3 py-2 rounded-sm border border-white/10"
          style={{ background: "rgba(4,7,12,0.85)", backdropFilter: "blur(6px)" }}
          data-testid="street-strike-legend"
        >
          {TIERS.map((t) => (
            <div key={t.id} className="flex items-center gap-1.5">
              <span
                className="inline-block w-2.5 h-2.5 rounded-full"
                style={{ background: t.accent, boxShadow: `0 0 8px ${t.accent}80` }}
              />
              <span className="text-[10px] uppercase tracking-[0.24em] font-mono-aviation text-slate-300">
                {t.label} · {t.price}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Selected shop drawer */}
      {active && (
        <div
          className="mt-5 rounded-sm border p-4 flex flex-col sm:flex-row sm:items-center gap-3"
          style={{
            borderColor: `${(TIERS.find((t) => t.id === active.tier) || TIERS[0]).accent}88`,
            background: "rgba(6,11,20,0.7)",
          }}
          data-testid="street-strike-selected"
        >
          <div className="flex-1 min-w-0">
            <div className="hud-label text-[10px] mb-1 flex items-center gap-2">
              <Compass size={11} /> Local Anchor · Target
            </div>
            <div className="font-display text-white text-base leading-tight">
              {active.trade} · <span className="text-slate-400">{active.street}</span>
            </div>
            <div className="mt-1.5 text-[11px] text-slate-400 leading-relaxed">
              Pitch the <span className="text-emerald-300">$97 Anchor</span> first.
              Once the concierge line is live, ladder them up to
              {" "}
              <span className="text-gold">
                {(TIERS.find((t) => t.id === active.tier) || TIERS[0]).label}
              </span>{" "}
              inside two quarters.
            </div>
          </div>
          <div
            className="text-right shrink-0"
            style={{ color: (TIERS.find((t) => t.id === active.tier) || TIERS[0]).accent }}
          >
            <TrendingUp size={16} className="inline mr-1" />
            <span className="font-display text-lg">
              {(TIERS.find((t) => t.id === active.tier) || TIERS[0]).price}
            </span>
          </div>
        </div>
      )}

      {/* Ladder */}
      <div className="mt-6" data-testid="street-strike-ladder">
        <div className="hud-label text-[10px] mb-3 text-gold flex items-center gap-2">
          <TrendingUp size={11} /> Anchor → Titan Ladder
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
          {TIERS.map((t, idx) => (
            <div
              key={t.id}
              className="rounded-sm border p-3"
              style={{
                borderColor: `${t.accent}55`,
                background:
                  `linear-gradient(160deg, ${t.accent}18 0%, rgba(6,11,20,0.6) 60%)`,
              }}
              data-testid={`street-strike-tier-${t.id}`}
            >
              <div className="flex items-center justify-between">
                <span
                  className="text-[10px] uppercase tracking-[0.24em] font-mono-aviation"
                  style={{ color: t.accent }}
                >
                  Tier {String(idx + 1).padStart(2, "0")}
                </span>
                <span className="text-slate-500 text-[10px]">{t.price}</span>
              </div>
              <div className="mt-1 font-display text-white text-sm">{t.label}</div>
              <div className="mt-1.5 text-[11px] text-slate-400 leading-relaxed">
                {t.desc}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div
        className="mt-5 text-[10px] uppercase tracking-[0.28em] font-mono-aviation text-slate-500"
        data-testid="street-strike-mock-notice"
      >
        · Mock geo dataset · Real API integration flagged for Aug rollout
      </div>
    </div>
  );
}
